import moment from "moment";

/**
 * Single source for the event-series card lifecycle.
 *
 * State is derived from the actual DATE WINDOWS + video_url — NOT from `status`
 * or `is_registration_open` alone, both of which go stale in the CMS (a series
 * left as status "upcoming" while its event is already running; is_registration_open
 * stuck at 1 after the registration window closed).
 *
 * Rule (first match wins):
 *   1. today within [event_start, event_end]           → video_url ? "Tonton Live" : "Berlangsung"
 *   2. today after event_end                           → "Udah Lewat"
 *   3. today within [reg_start, reg_end] (& reg open)   → "Registrasi"
 *   4. otherwise (before registration, or reg closed but pre-event) → "Akan Datang"
 */

export type SeriesCardAction = "register" | "watch" | "detail" | "none";

export interface SeriesCardView {
  badge: string;
  badgeBgColor: string;
  grayScale: boolean;
  buttonText: string;
  buttonDisabled: boolean;
  buttonBgColor: string;
  buttonBorderColor: string;
  buttonTextColor: string;
  action: SeriesCardAction;
}

interface SeriesCardInput {
  event_start_date?: string;
  event_end_date?: string;
  registration_start_date?: string | null;
  registration_end_date?: string | null;
  video_url?: string | null;
  is_registration_open?: boolean | number;
}

const REGISTRASI = (days: number): SeriesCardView => ({
  badge: days > 0 ? `${days} Hari Lagi` : "Berlangsung",
  badgeBgColor: "#00BCF1",
  grayScale: false,
  buttonText: "Registrasi",
  buttonDisabled: false,
  buttonBgColor: "#00BCF1",
  buttonBorderColor: "#00BCF1",
  buttonTextColor: "#fff",
  action: "register",
});

const LIHAT_DETAIL = (badge: string, badgeBgColor: string): SeriesCardView => ({
  badge,
  badgeBgColor,
  grayScale: false,
  buttonText: "Lihat Detail",
  buttonDisabled: false,
  buttonBgColor: "#fff",
  buttonBorderColor: "#00BCF1",
  buttonTextColor: "#00BCF1",
  action: "detail",
});

const BERLANGSUNG = LIHAT_DETAIL("Berlangsung", "#1F2D3D");

const TONTON_LIVE: SeriesCardView = {
  badge: "LIVE",
  badgeBgColor: "#FF110D",
  grayScale: false,
  buttonText: "Tonton Live",
  buttonDisabled: false,
  buttonBgColor: "#00BCF1",
  buttonBorderColor: "transparent",
  buttonTextColor: "#fff",
  action: "watch",
};

const UDAH_LEWAT: SeriesCardView = {
  badge: "Udah Lewat",
  badgeBgColor: "rgba(31, 45, 61, 0.70)",
  grayScale: false,
  buttonText: "Lihat Detail",
  buttonDisabled: false,
  buttonBgColor: "transparent",
  buttonBorderColor: "#1F2D3D",
  buttonTextColor: "#1F2D3D",
  action: "detail",
};

const AKAN_DATANG: SeriesCardView = {
  badge: "AKAN DATANG",
  badgeBgColor: "#6A7481",
  grayScale: true,
  buttonText: "Akan Datang",
  buttonDisabled: true,
  buttonBgColor: "transparent",
  buttonBorderColor: "#6A7481",
  buttonTextColor: "#6A7481",
  action: "none",
};

export const getSeriesCardState = (data: SeriesCardInput = {}): SeriesCardView => {
  const {
    event_start_date,
    event_end_date,
    registration_start_date,
    registration_end_date,
    video_url,
    is_registration_open,
  } = data;

  const now = moment();
  const eventStart = moment(event_start_date);
  const eventEnd = moment(event_end_date);

  // 1. Event is running now → live stream if available, else "Berlangsung".
  if (
    event_start_date &&
    event_end_date &&
    now.isBetween(eventStart, eventEnd, "day", "[]")
  ) {
    return video_url ? TONTON_LIVE : BERLANGSUNG;
  }

  // 2. Event finished.
  if (event_end_date && now.isAfter(eventEnd, "day")) {
    return UDAH_LEWAT;
  }

  // 3. Registration window is open now (dates + flag, so a stale flag alone
  //    can't force it and an early close is still respected).
  if (
    registration_start_date &&
    registration_end_date &&
    is_registration_open &&
    now.isBetween(registration_start_date, registration_end_date, "day", "[]")
  ) {
    const diffHours = eventStart.diff(now, "hours");
    const diffDays = eventStart.diff(now, "days");
    const daysToEvent = diffHours <= 0 ? diffDays : diffDays + 1;
    return REGISTRASI(daysToEvent);
  }

  // 4. Before registration, or registration closed but the event hasn't started.
  return AKAN_DATANG;
};
